import { Activity } from '@breath-lab/types';
import { Breathwork } from '@breath-lab/types';
import { Filters, hasNoFilters } from '@breath-lab/types';
import { LocalizedString } from '@breath-lab/types';
import { TrainingBenefit } from '@breath-lab/types';

export function filterBreathworks(
  breathworks: Breathwork[],
  query: string,
  filters: Filters,
  activities: Activity[] = [],
  benefits: TrainingBenefit[] = [],
): Breathwork[] {
  let results = breathworks.filter((b) => b.isEnabled);
  const normalizedQuery = normalizeSearchText(query);

  if (normalizedQuery.length > 0) {
    results = searchBreathworks(results, normalizedQuery, activities, benefits);
  }

  if (!hasNoFilters(filters)) {
    if (filters.selectedActivitiesIds.length > 0) {
      results = results.filter((b) =>
        filters.selectedActivitiesIds.every((id) => b.activitiesIds.includes(id)),
      );
    }

    if (filters.selectedBenefitIds.length > 0) {
      results = results.filter((b) =>
        filters.selectedBenefitIds.every((id) => b.benefitsIds.includes(id)),
      );
    }

    if (filters.selectedDifficulties.length > 0) {
      results = results.filter(
        (b) => b.difficultyLevel != null && filters.selectedDifficulties.every((d) => d === b.difficultyLevel),
      );
    }
  }

  if (normalizedQuery.length > 0) {
    return results;
  }

  return results.slice().sort((a, b) => a.position - b.position);
}

function searchBreathworks(
  breathworks: Breathwork[],
  query: string,
  activities: Activity[],
  benefits: TrainingBenefit[],
): Breathwork[] {
  const activityNamesById = new Map(
    activities.map((activity) => [activity.id, collectLocalizedTerms(activity.name)]),
  );
  const benefitNamesById = new Map(
    benefits.map((benefit) => [benefit.id, uniqueTerms(Object.values(benefit.name))]),
  );

  return breathworks
    .map((breathwork) => ({
      breathwork,
      rank: getSearchRank(breathwork, query, activityNamesById, benefitNamesById),
    }))
    .filter((item): item is { breathwork: Breathwork; rank: number } => item.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.breathwork.position - b.breathwork.position)
    .map((item) => item.breathwork);
}

function getSearchRank(
  breathwork: Breathwork,
  query: string,
  activityNamesById: Map<string, string[]>,
  benefitNamesById: Map<string, string[]>,
): number | null {
  const primaryRank = rankTerms(collectLocalizedTerms(breathwork.name), query, 0);
  const secondaryRank = rankTerms(
    breathwork.secondaryNames.flatMap(collectLocalizedTerms),
    query,
    1,
  );
  const taxonomyRank = rankTerms(
    [
      ...breathwork.activitiesIds.flatMap((id) => activityNamesById.get(id) ?? []),
      ...breathwork.benefitsIds.flatMap((id) => benefitNamesById.get(id) ?? []),
    ],
    query,
    2,
  );

  return [primaryRank, secondaryRank, taxonomyRank].reduce<number | null>((best, rank) => {
    if (rank === null) return best;
    if (best === null || rank < best) return rank;
    return best;
  }, null);
}

function rankTerms(terms: string[], query: string, fieldPriority: number): number | null {
  return terms.reduce<number | null>((best, term) => {
    const strength = matchStrength(term, query);
    if (strength === null) return best;

    const rank = strength * 10 + fieldPriority;
    if (best === null || rank < best) return rank;
    return best;
  }, null);
}

function matchStrength(term: string, query: string): number | null {
  const normalizedTerm = normalizeSearchText(term);
  if (!normalizedTerm) return null;

  if (normalizedTerm === query) return 0;
  if (normalizedTerm.startsWith(query)) return 1;

  const tokens = normalizedTerm.split(' ');
  if (query.indexOf(' ') === -1 && tokens.some((token) => token.startsWith(query))) {
    return 1;
  }

  if (normalizedTerm.includes(query)) return 2;
  return null;
}

function collectLocalizedTerms(value: LocalizedString): string[] {
  return uniqueTerms([value.en, value.saEn, value.sanskrit]);
}

function uniqueTerms(values: Array<string | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
}

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}
